import ContextualSaveBar from "@/components/bars/ContextualSaveBar";
import { Card, CardContent, CardHeader } from "@/components/layouts/Card";
import PageLoader from "@/components/loader/PageLoader";
import CategorySelectionModal from "@/components/modals/CategorySelectionModal";
import ProductSelectionModal from "@/components/modals/ProductSelectionModal";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

interface Product {
  id: number;
  name: string;
  menu_display_name: string;
}

interface Category {
  id: number;
  name: string;
}

interface Printer {
  id: number;
  name: string;
  is_primary: boolean;
  is_report_printer: boolean;
  printer_id: number;
  state: string;
  products: Product[];
  categories: Category[];
  printing_copies: number;
}

const EditPrinter = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { id: printerId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [axiosInstance, loading, isFetching] = useAxiosInstance();
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [printer, setPrinter] = useState<Printer>({
    id: 0,
    name: "",
    is_primary: false,
    is_report_printer: false,
    printer_id: 0,
    state: "",
    products: [],
    categories: [],
    printing_copies: 0,
  });
  const [originalPrinter, setOriginalPrinter] = useState<Printer | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchPrinter = async () => {
      try {
        if (!loading && axiosInstance && printerId) {
          const response = await axiosInstance.get(
            `print/printers/${printerId}/`,
          );
          setPrinter(response.data);
          setOriginalPrinter(response.data);
        }
      } catch (error) {
        console.error("Error fetching printer:", error);
        toast.error("Failed to fetch printer details.");
      }
    };

    if (printerId) {
      fetchPrinter();
    }
  }, [axiosInstance, loading, printerId]);

  useEffect(() => {
    if (originalPrinter) {
      const isChanged =
        JSON.stringify(printer) !== JSON.stringify(originalPrinter);
      setIsDirty(isChanged);
    }
  }, [printer, originalPrinter]);

  const handleRemoveCategory = (categoryId: number) => {
    setPrinter((prev) => ({
      ...prev,
      categories: prev.categories.filter((c) => c.id !== categoryId),
    }));
    setIsDirty(true);
  };

  const handleRemoveProduct = (productId: number) => {
    setPrinter((prev) => ({
      ...prev,
      products: prev.products.filter((p) => p.id !== productId),
    }));
    setIsDirty(true);
  };

  const handleUpdateCategories = (updatedCategoryIds: number[]) => {
    const newCategories = updatedCategoryIds.map((id) => {
      const category = printer.categories.find((c) => c.id === id);
      return category ? category : { id, name: "" };
    });
    setPrinter((prev) => ({ ...prev, categories: newCategories }));
  };

  const handleUpdateProducts = (updatedProductIds: number[]) => {
    const newProducts = updatedProductIds.map((id) => {
      const product = printer.products.find((p) => p.id === id);
      return product ? product : { id, name: "", menu_display_name: "" };
    });
    setPrinter((prev) => ({ ...prev, products: newProducts }));
  };

  const handleUpdatePrinter = async () => {
    setIsSaving(true);
    try {
      if (!loading && axiosInstance && user?.selectedBranch) {
        const updatedPrinter = {
          is_primary: printer.is_primary,
          is_report_printer: printer.is_report_printer,
          product_ids: printer.products.map((p) => p.id),
          category_ids: printer.categories.map((c) => c.id),
          printing_copies: printer.printing_copies,
        };

        const response = await axiosInstance.patch(
          `print/printers/${printerId}/update_printer/`,
          updatedPrinter,
        );

        setPrinter(response.data);
        setOriginalPrinter(response.data);
        setIsDirty(false);

        toast.success("Printer updated successfully");
      }
    } catch (error) {
      console.error("Error updating printer:", error);
      toast.error("Failed to update printer");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (originalPrinter) {
      setPrinter(originalPrinter);
      setIsDirty(false);
    }
  };

  const handleCategorySave = (selectedCategories: Category[]) => {
    setPrinter((prev) => ({ ...prev, categories: selectedCategories }));
    setIsCategoryModalOpen(false);
  };

  const handleProductSave = (selectedProducts: Product[]) => {
    setPrinter((prev) => ({ ...prev, products: selectedProducts }));
    setIsProductModalOpen(false);
  };

  const handlePrintingCopiesChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const value = parseInt(e.target.value);
    setPrinter((prev) => ({
      ...prev,
      printing_copies: value,
    }));
  };

  return (
    <PageLoader isFetching={isFetching}>
      <div className="container mx-auto px-4 py-2">
        {printerId ? (
          <>
            <h1 className="mb-4 text-xl font-bold">
              {t("back.management.printer.editPrinter")}
            </h1>

            {/* General Information Section */}
            <Card className="mb-4">
              <CardHeader>
                <h2 className="text-lg font-semibold">
                  {t("back.management.printer.generalInfo")}
                </h2>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <label
                    className="mb-2 block text-sm font-bold text-gray-700"
                    htmlFor="printerName"
                  >
                    {t("back.management.printer.printerName")}
                  </label>
                  <input
                    type="text"
                    id="printerName"
                    value={printer.name}
                    readOnly
                    className="block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 shadow-sm"
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="mb-2 block text-sm font-bold text-gray-700"
                    htmlFor="printerId"
                  >
                    {t("back.management.printer.printerId")}
                  </label>
                  <input
                    type="number"
                    id="printerId"
                    value={printer.printer_id}
                    readOnly
                    className="block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 shadow-sm"
                  />
                </div>
                <div className="mb-4">
                  <label
                    className="mb-2 block text-sm font-bold text-gray-700"
                    htmlFor="printingCopies"
                  >
                    {t("back.management.printer.printingCopies")}
                  </label>
                  <select
                    id="printingCopies"
                    value={printer.printing_copies}
                    onChange={handlePrintingCopiesChange}
                    disabled={!printer.is_primary}
                    className={`block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm ${
                      !printer.is_primary ? "bg-gray-100" : "bg-white"
                    }`}
                  >
                    {[...Array(9)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={printer.is_primary}
                      onChange={(e) =>
                        setPrinter({ ...printer, is_primary: e.target.checked })
                      }
                      className="form-checkbox h-5 w-5 text-red-600"
                    />
                    <span className="ml-2 font-semibold text-gray-700">
                      {t("back.management.printer.isPrimary")}
                    </span>
                  </label>
                </div>
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={printer.is_report_printer}
                      onChange={(e) =>
                        setPrinter({
                          ...printer,
                          is_report_printer: e.target.checked,
                        })
                      }
                      className="form-checkbox h-5 w-5 text-red-600"
                    />
                    <span className="ml-2 font-semibold text-gray-700">
                      {t("back.management.printer.isReportPrinter")}
                    </span>
                  </label>
                </div>
              </CardContent>
            </Card>

            {/* Status Section */}
            <Card className="mb-4">
              <CardHeader>
                <h2 className="text-lg font-semibold">
                  {t("back.management.printer.status")}
                </h2>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  {t("back.management.printer.currentStatus")}: {printer.state}
                </p>
              </CardContent>
            </Card>

            {!printer.is_primary && (
              <>
                {/* Linked Category Section */}
                <Card className="mb-4">
                  <CardHeader className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Linked Category</h2>
                    <button
                      onClick={() => setIsCategoryModalOpen(true)}
                      className="flex items-center rounded-md bg-gray-500 px-3 py-1 text-white"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      Edit
                    </button>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {printer.categories.map((category) => (
                        <span
                          key={category.id}
                          className="flex items-center rounded-full bg-red-100 px-2 py-1 text-sm text-red-500"
                        >
                          {category.name}
                          <button
                            onClick={() => handleRemoveCategory(category.id)}
                            className="ml-2 text-red-500 hover:text-red-700"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Linked Product Section */}
                <Card className="mb-4">
                  <CardHeader className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">Linked Product</h2>
                    <button
                      onClick={() => setIsProductModalOpen(true)}
                      className="flex items-center rounded-md bg-gray-500 px-3 py-1 text-white"
                    >
                      <FontAwesomeIcon icon={faPlus} className="mr-2" />
                      Edit
                    </button>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {printer.products.map((product) => (
                        <span
                          key={product.id}
                          className="flex items-center rounded-full bg-red-100 px-2 py-1 text-sm text-red-500"
                        >
                          {product.menu_display_name}
                          <button
                            onClick={() => handleRemoveProduct(product.id)}
                            className="ml-2 text-red-500 hover:text-red-700"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Action Buttons */}
            <div className="mt-4 flex justify-end">
              <Link
                to="/printer"
                className="mr-2 rounded-md bg-gray-500 px-4 py-2 text-white"
              >
                {t("back.management.printer.cancelButton")}
              </Link>
              <button
                onClick={handleUpdatePrinter}
                className="rounded-md bg-red-500 px-4 py-2 text-white"
                disabled={!isDirty || isSaving}
              >
                {t("back.management.printer.updateButton")}
              </button>
            </div>
          </>
        ) : (
          <p>{t("back.management.printer.noPrinterSelected")}</p>
        )}

        {!printer.is_primary && (
          <>
            <CategorySelectionModal
              isOpen={isCategoryModalOpen}
              onClose={() => setIsCategoryModalOpen(false)}
              onSave={handleCategorySave}
              initialSelectedCategories={printer.categories.map((c) => c.id)}
            />

            <ProductSelectionModal
              isOpen={isProductModalOpen}
              onClose={() => setIsProductModalOpen(false)}
              onSave={handleProductSave}
              initialSelectedProducts={printer.products.map((p) => p.id)}
            />
          </>
        )}

        <ContextualSaveBar
          isDirty={isDirty}
          onSave={handleUpdatePrinter}
          onDiscard={handleDiscard}
          isSaving={isSaving}
        />
      </div>
    </PageLoader>
  );
};

export default EditPrinter;
